import { getBackendUrl } from '@/utils/apiUrl';
import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Home, FileText, Plus, Settings, LogOut, Search, Bell, User, Building, HeadphonesIcon, MessageSquare, Lightbulb, CreditCard, Users, CheckSquare, Calendar, GraduationCap, Menu, X, Archive, Clock, Mail, FolderOpen } from 'lucide-react';

const Layout = ({ children, user }) => {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showNewCaseDropdown, setShowNewCaseDropdown] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Auto-collapse sidebar on /new-case route
  const isNewCaseRoute = location.pathname === '/new-case';
  const [sidebarOpen, setSidebarOpen] = useState(!isNewCaseRoute);

  // Widgets State
  const [currentTime, setCurrentTime] = useState(new Date());
  const [activeCases, setActiveCases] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [hasNewNotification, setHasNewNotification] = useState(false);
  const [companyData, setCompanyData] = useState(null);

  // Auto-close sidebar when navigating to /new-case
  React.useEffect(() => {
    if (isNewCaseRoute) {
      setSidebarOpen(false);
    }
  }, [isNewCaseRoute]);

  // Close dropdown when route changes
  React.useEffect(() => {
    setShowNewCaseDropdown(false);
    setShowUserMenu(false);
  }, [location.pathname]);

  // Close dropdowns when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      // Close New Case dropdown
      if (showNewCaseDropdown && !event.target.closest('[data-new-case-dropdown]')) {
        setShowNewCaseDropdown(false);
      }
      // Close User Menu dropdown
      if (showUserMenu && !event.target.closest('[data-user-menu]')) {
        setShowUserMenu(false);
      }
      // Close Notifications dropdown
      if (showNotifications && !event.target.closest('[data-notifications]')) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showNewCaseDropdown, showUserMenu, showNotifications]);

  // Update time every second
  React.useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Fetch active cases count
  React.useEffect(() => {
    const fetchActiveCases = async () => {
      try {
        const response = await fetch(getBackendUrl() + '/api/cases', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          const active = data.filter(c => c.status === 'neu' || c.status === 'in-bearbeitung').length;
          setActiveCases(active);
        }
      } catch (error) {
        console.error('Error fetching cases:', error);
      }
    };
    fetchActiveCases();
  }, []);

  // Fetch company data for user menu
  React.useEffect(() => {
    const fetchCompanyData = async () => {
      try {
        const response = await fetch(getBackendUrl() + '/api/company-data', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          setCompanyData(data);
        }
      } catch (error) {
        console.error('Error fetching company data:', error);
      }
    };
    fetchCompanyData();
  }, []);

  // Simulate notification
  const simulateNotification = () => {
    const newNotif = {
      id: Date.now(),
      message: 'Neue Nachricht vom Admin',
      timestamp: new Date()
    };
    setNotifications(prev => [newNotif, ...prev]);
    setHasNewNotification(true);
    
    // Reset animation after 2 seconds
    setTimeout(() => setHasNewNotification(false), 2000);
  };

  const getWeekNumber = (date) => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(),0,1));
    return Math.ceil((((d - yearStart) / 86400000) + 1)/7);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    // Force reload to clear any cached state
    window.location.href = '/login';
  };

  const isActive = (path) => location.pathname === path;

  const navItems = [
    { path: '/', icon: FileText, label: 'Akten', testId: 'nav-cases' },
    { path: '/accounting', icon: CreditCard, label: 'Buchhaltung', testId: 'nav-accounting' },
    { path: '/customers', icon: Users, label: 'Kunden', testId: 'nav-customers' },
    { path: '/tasks', icon: CheckSquare, label: 'Aufgaben', testId: 'nav-tasks' },
    { path: '/appointments', icon: Calendar, label: 'Termine', testId: 'nav-appointments' },
    { path: '/archive', icon: Archive, label: 'Archiv', testId: 'nav-archive' },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F5F7FA' }}>
      {/* Header */}
      <header className="bg-white border-b fixed w-full top-0 z-50" style={{ borderColor: '#E1E4E8', height: '64px' }}>
        <div className="h-full flex items-center justify-between px-4 md:px-6">
          {/* Left side */}
          <div className="flex items-center gap-2 md:gap-6">
            {/* Mobile Menu Button */}
            <button
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className="lg:hidden p-2 hover:bg-gray-100 rounded-lg"
              data-testid="mobile-menu-button"
            >
              {showMobileMenu ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>

            {/* Desktop Sidebar Toggle */}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="hidden lg:block p-2 hover:bg-gray-100 rounded-lg"
              data-testid="sidebar-toggle-button"
            >
              <Menu className="w-6 h-6" />
            </button>

            {/* Logo */}
            <Link to="/" className="flex items-center gap-2">
              <img 
                src="/easyclaim24-clean.png" 
                alt="Easyclaim24 Logo" 
                className="h-10 md:h-12 w-auto object-contain"
                data-testid="header-logo"
              />
            </Link>

            {/* Dashboard link - Hidden on mobile */}
            <Link 
              to="/" 
              className="hidden md:block text-base font-medium transition-colors"
              style={{ color: isActive('/') ? '#0A4D8C' : '#4A5568' }}
              data-testid="header-dashboard"
            >
              Dashboard
            </Link>

            {/* New Case Button with Dropdown */}
            <div className="relative" data-new-case-dropdown>
              <button
                type="button"
                onClick={() => setShowNewCaseDropdown(!showNewCaseDropdown)}
                className="flex items-center gap-1 px-2 md:px-3 py-1.5 text-white text-xs md:text-sm font-medium rounded-md transition-all hover:shadow-md"
                style={{ backgroundColor: '#0A4D8C' }}
                data-testid="header-new-case"
              >
                <Plus className="w-3.5 h-3.5 md:w-4 md:h-4" />
                <span className="hidden sm:inline">Neuer Fall</span>
                <svg className={`w-3 h-3 ml-1 hidden sm:block transition-transform ${showNewCaseDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {/* Dropdown Menu */}
              {showNewCaseDropdown && (
                <div className="absolute left-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-50 min-w-[200px]">
                  <div className="py-2">
                    <Link
                      to={`/new-case?auftragstyp=austausch&new=${Date.now()}`}
                      onClick={() => setShowNewCaseDropdown(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 transition-colors"
                      data-testid="new-case-austausch"
                    >
                      <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                        <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">Austausch</div>
                        <div className="text-xs text-gray-500">Scheibenaustausch</div>
                      </div>
                    </Link>
                    
                    <Link
                      to={`/new-case?auftragstyp=reparatur&new=${Date.now()}`}
                      onClick={() => setShowNewCaseDropdown(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 transition-colors"
                      data-testid="new-case-reparatur"
                    >
                      <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                        <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">Reparatur</div>
                        <div className="text-xs text-gray-500">Reparaturauftrag anlegen</div>
                      </div>
                    </Link>
                    
                    <Link
                      to={`/new-case?auftragstyp=gutachten&new=${Date.now()}`}
                      onClick={() => setShowNewCaseDropdown(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 transition-colors"
                      data-testid="new-case-gutachten"
                    >
                      <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">Kostenvoranschlag</div>
                        <div className="text-xs text-gray-500">Kostenvoranschlag erstellen</div>
                      </div>
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right side */}
          <div className="flex items-center gap-2 md:gap-4">
            {/* Widgets */}
            <div className="hidden lg:flex items-center gap-3">
              {/* Time Widget */}
              <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 rounded-lg border border-blue-200" data-testid="time-widget">
                <Clock className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-semibold text-blue-900">
                  {currentTime.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>

              {/* Date & Week Widget */}
              <div className="flex items-center gap-2 px-3 py-1.5 bg-purple-50 rounded-lg border border-purple-200" data-testid="date-widget">
                <Calendar className="w-4 h-4 text-purple-600" />
                <span className="text-xs font-medium text-purple-900">
                  {currentTime.toLocaleDateString('de-DE', { day: '2-digit', month: 'short' })} • KW{getWeekNumber(currentTime)}
                </span>
              </div>

              {/* Active Cases Widget */}
              <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 rounded-lg border border-green-200" data-testid="active-cases-widget">
                <FileText className="w-4 h-4 text-green-600" />
                <span className="text-xs font-medium text-green-900">
                  {activeCases} Aktiv
                </span>
              </div>
            </div>

            {/* Search - Hidden on small mobile */}
            <div className="hidden sm:block relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Suchen..."
                className="pl-10 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-32 md:w-64"
                style={{ borderColor: '#E1E4E8' }}
                data-testid="search-input"
              />
            </div>

            {/* Notifications */}
            <div className="relative" data-notifications>
              <button 
                onClick={() => setShowNotifications(!showNotifications)}
                className={`relative p-2 hover:bg-gray-100 rounded-lg transition-all ${
                  hasNewNotification ? 'animate-wiggle' : ''
                }`}
                data-testid="notifications-button"
              >
                <Bell className="w-5 h-5 text-gray-600" />
                {notifications.length > 0 && (
                  <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                )}
              </button>

              {/* Notifications Dropdown */}
              {showNotifications && (
                <>
                  <div 
                    className="fixed inset-0 z-40"
                    onClick={() => setShowNotifications(false)}
                  ></div>
                  <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-50">
                    <div className="p-4 border-b border-gray-200">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-gray-900">Benachrichtigungen</h3>
                        <button
                          onClick={simulateNotification}
                          className="text-xs text-blue-600 hover:text-blue-700"
                        >
                          Test
                        </button>
                      </div>
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                      {notifications.length === 0 ? (
                        <div className="p-8 text-center text-gray-500">
                          <Bell className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                          <p className="text-sm">Keine Benachrichtigungen</p>
                        </div>
                      ) : (
                        notifications.map(notif => (
                          <div key={notif.id} className="p-4 border-b border-gray-100 hover:bg-gray-50">
                            <p className="text-sm text-gray-900">{notif.message}</p>
                            <p className="text-xs text-gray-500 mt-1">
                              {notif.timestamp.toLocaleTimeString('de-DE')}
                            </p>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* User Menu */}
            <div className="relative" data-user-menu>
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center gap-2 md:gap-3 p-1 md:p-2 hover:bg-gray-100 rounded-lg transition-colors"
                data-testid="user-menu-button"
              >
                {companyData?.logo_url ? (
                  <div className="w-8 h-8 md:w-10 md:h-10 rounded-full overflow-hidden border-2 border-gray-200 flex items-center justify-center bg-white">
                    <img 
                      src={getBackendUrl() + companyData.logo_url} 
                      alt="Firmenlogo" 
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center text-white font-semibold text-sm" style={{ backgroundColor: '#0A4D8C' }}>
                    {user?.name?.charAt(0) || 'A'}
                  </div>
                )}
                <div className="text-left hidden md:block">
                  <div className="text-sm font-semibold text-gray-900">{companyData?.company_name || user?.name || 'Admin Demo'}</div>
                  <div className="text-xs text-gray-500">{user?.role || 'admin'}</div>
                </div>
              </button>

              {/* Dropdown Menu */}
              {showUserMenu && (
                <>
                  {/* Backdrop for mobile */}
                  <div 
                    className="fixed inset-0 z-40 md:hidden"
                    onClick={() => setShowUserMenu(false)}
                  ></div>
                  <div 
                    className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-xl border overflow-hidden z-50"
                    style={{ borderColor: '#E1E4E8' }}
                  >
                    <div className="px-4 py-3 border-b" style={{ borderColor: '#E1E4E8' }}>
                      {/* Firmenlogo im Benutzermenü */}
                      {companyData?.logo_url && (
                        <div className="flex justify-center mb-3 pb-3 border-b" style={{ borderColor: '#E1E4E8' }}>
                          <img 
                            src={getBackendUrl() + companyData.logo_url} 
                            alt="Firmenlogo" 
                            className="h-12 w-auto object-contain"
                          />
                        </div>
                      )}
                      <div className="font-semibold text-gray-900">{user?.name || 'Admin Demo'}</div>
                      <div className="text-sm text-gray-500">{user?.email || 'admin@easyclaim24.de'}</div>
                      {companyData?.company_name && (
                        <div className="mt-1 text-sm font-medium" style={{ color: '#0A4D8C' }}>
                          <Building className="w-3 h-3 inline mr-1" />
                          {companyData.company_name}
                        </div>
                      )}
                    </div>

                    <div className="py-2">
                      <Link 
                        to="/settings" 
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
                        onClick={() => setShowUserMenu(false)}
                        data-testid="menu-settings"
                      >
                        <Settings className="w-4 h-4" />
                        Einstellungen
                      </Link>
                      {/* Super Admin Link entfernt - nur über direkte URL /super-admin erreichbar */}
                      <Link 
                        to="/training" 
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
                        onClick={() => setShowUserMenu(false)}
                        data-testid="menu-training"
                      >
                        <GraduationCap className="w-4 h-4" />
                        Schulung
                      </Link>
                      <Link 
                        to="/support" 
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
                        onClick={() => setShowUserMenu(false)}
                        data-testid="menu-support"
                      >
                        <HeadphonesIcon className="w-4 h-4" />
                        Support
                      </Link>
                      <Link 
                        to="/chat-support" 
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
                        onClick={() => setShowUserMenu(false)}
                        data-testid="menu-chat"
                      >
                        <MessageSquare className="w-4 h-4" />
                        Chat
                      </Link>
                      <Link 
                        to="/improvements" 
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
                        onClick={() => setShowUserMenu(false)}
                        data-testid="menu-improvements"
                      >
                        <Lightbulb className="w-4 h-4" />
                        Verbesserung
                      </Link>
                    </div>

                    <div className="border-t" style={{ borderColor: '#E1E4E8' }}>
                      <Link 
                        to="/impressum" 
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
                        onClick={() => setShowUserMenu(false)}
                        data-testid="menu-impressum"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Impressum
                      </Link>
                      <Link 
                        to="/datenschutz" 
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
                        onClick={() => setShowUserMenu(false)}
                        data-testid="menu-datenschutz"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                        Datenschutz
                      </Link>
                      <Link 
                        to="/barrierefreiheit" 
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
                        onClick={() => setShowUserMenu(false)}
                        data-testid="menu-barrierefreiheit"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636a9 9 0 010 12.728m0 0l-2.829-2.829m2.829 2.829L21 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.829m-4.243 2.829a4.978 4.978 0 01-1.414-2.83m-1.414 5.658a9 9 0 01-2.167-9.238m7.824 2.167a1 1 0 111.414 1.414m-1.414-1.414L3 3m8.293 8.293l1.414 1.414" />
                        </svg>
                        Barrierefreiheit
                      </Link>
                    </div>

                    <div className="border-t" style={{ borderColor: '#E1E4E8' }}>
                      <button
                        onClick={handleLogout}
                        className="w-full px-4 py-3 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-3 font-medium"
                        data-testid="menu-logout"
                      >
                        <LogOut className="w-4 h-4" />
                        Abmelden
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {showMobileMenu && (
        <>
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
            style={{ top: '64px' }}
            onClick={() => setShowMobileMenu(false)}
          ></div>
          <div 
            className="fixed left-0 top-0 w-64 bg-white h-full shadow-xl z-50 lg:hidden"
            style={{ top: '64px' }}
          >
            <nav className="p-4 space-y-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setShowMobileMenu(false)}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg transition-all text-sm font-medium"
                    style={{
                      backgroundColor: active ? '#0A4D8C' : 'transparent',
                      color: active ? 'white' : '#4A5568'
                    }}
                    data-testid={item.testId}
                  >
                    <Icon className="w-5 h-5" />
                    {item.label}
                  </Link>
                );
              })}

              <div className="pt-4 mt-4 border-t" style={{ borderColor: '#E1E4E8' }}>
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg transition-all text-sm font-medium w-full text-left text-red-600 hover:bg-red-50"
                  data-testid="sidebar-logout"
                >
                  <LogOut className="w-5 h-5" />
                  Abmelden
                </button>
              </div>
            </nav>
          </div>
        </>
      )}

      {/* Desktop Sidebar */}
      <aside 
        className={`hidden lg:block bg-white border-r fixed left-0 h-full transition-all duration-300 ${
          sidebarOpen ? 'w-64' : 'w-16'
        }`} 
        style={{ borderColor: '#E1E4E8', top: '64px' }}
      >
        {/* Toggle Button */}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="absolute -right-3 top-8 w-6 h-6 bg-white border border-gray-300 rounded-full flex items-center justify-center shadow-sm hover:bg-gray-50 transition-colors z-10"
          data-testid="sidebar-toggle"
          title={sidebarOpen ? 'Sidebar einklappen' : 'Sidebar ausklappen'}
        >
          <svg 
            className={`w-4 h-4 text-gray-600 transition-transform duration-300 ${sidebarOpen ? '' : 'rotate-180'}`} 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        {/* Logo über der Sidebar */}
        <div className={`flex items-center justify-center border-b transition-all duration-300 ${
          sidebarOpen ? 'p-6' : 'p-3'
        }`} style={{ borderColor: '#E1E4E8' }}>
          <img 
            src="/easyclaim24-clean.png" 
            alt="Easyclaim24 Logo" 
            className={`object-contain transition-all duration-300 ${
              sidebarOpen ? 'h-20 w-auto' : 'h-12 w-12'
            }`}
            data-testid="sidebar-logo"
          />
        </div>
        
        <nav className="p-4 space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center rounded-lg transition-all text-sm font-medium ${
                  sidebarOpen ? 'gap-3 px-4 py-3' : 'justify-center p-3'
                }`}
                style={{
                  backgroundColor: active ? '#0A4D8C' : 'transparent',
                  color: active ? 'white' : '#4A5568'
                }}
                data-testid={item.testId}
                title={!sidebarOpen ? item.label : ''}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                {sidebarOpen && <span>{item.label}</span>}
              </Link>
            );
          })}

          <div className={`pt-4 mt-4 border-t ${sidebarOpen ? '' : 'border-transparent'}`} style={{ borderColor: sidebarOpen ? '#E1E4E8' : 'transparent' }}>
            <button
              onClick={handleLogout}
              className={`flex items-center rounded-lg transition-all text-sm font-medium w-full text-left text-red-600 hover:bg-red-50 ${
                sidebarOpen ? 'gap-3 px-4 py-3' : 'justify-center p-3'
              }`}
              data-testid="sidebar-logout"
              title={!sidebarOpen ? 'Abmelden' : ''}
            >
              <LogOut className="w-5 h-5 flex-shrink-0" />
              {sidebarOpen && <span>Abmelden</span>}
            </button>
          </div>
        </nav>
      </aside>

      {/* Main Content */}
      <main 
        className={`min-h-screen transition-all duration-300 ${
          sidebarOpen ? 'lg:ml-64' : 'lg:ml-16'
        }`}
        style={{ paddingTop: '64px' }}
      >
        <div className="px-4 sm:px-6 md:px-8 lg:px-6 py-4 md:py-6">
          <div className="lg:max-w-[1700px] lg:mx-auto w-full">
            {children}
          </div>
        </div>
      </main>
      
      {/* Mobile Bottom Navigation - nur auf kleinen Bildschirmen und NICHT auf NewCase-Seite */}
      {!isNewCaseRoute && (
        <nav className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg z-50 lg:hidden" style={{ borderColor: '#E1E4E8' }}>
          <div className="flex items-center justify-around py-2 px-1">
            <Link 
              to="/" 
              className={`flex flex-col items-center justify-center min-w-[60px] py-1 px-2 rounded-lg transition-colors ${
                isActive('/') ? 'text-blue-600 bg-blue-50' : 'text-gray-600 hover:bg-gray-50'
              }`}
              data-testid="mobile-nav-dashboard"
            >
              <Home className="w-5 h-5 mb-0.5" />
              <span className="text-[10px] font-medium">Start</span>
            </Link>
            
            <Link 
              to="/cases" 
              className={`flex flex-col items-center justify-center min-w-[60px] py-1 px-2 rounded-lg transition-colors ${
                isActive('/cases') ? 'text-blue-600 bg-blue-50' : 'text-gray-600 hover:bg-gray-50'
              }`}
              data-testid="mobile-nav-cases"
            >
              <FolderOpen className="w-5 h-5 mb-0.5" />
              <span className="text-[10px] font-medium">Akten</span>
            </Link>
            
            <button
              onClick={() => setShowNewCaseDropdown(!showNewCaseDropdown)}
              className="flex flex-col items-center justify-center min-w-[60px] py-1 px-3 rounded-full text-white transition-colors"
              style={{ backgroundColor: '#0A4D8C' }}
              data-testid="mobile-nav-new"
            >
            <Plus className="w-6 h-6" />
            <span className="text-[10px] font-medium">Neu</span>
          </button>
          
          <Link 
            to="/customers" 
            className={`flex flex-col items-center justify-center min-w-[60px] py-1 px-2 rounded-lg transition-colors ${
              isActive('/customers') ? 'text-blue-600 bg-blue-50' : 'text-gray-600 hover:bg-gray-50'
            }`}
            data-testid="mobile-nav-customers"
          >
            <Users className="w-5 h-5 mb-0.5" />
            <span className="text-[10px] font-medium">Kunden</span>
          </Link>
          
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            className={`flex flex-col items-center justify-center min-w-[60px] py-1 px-2 rounded-lg transition-colors ${
              showUserMenu ? 'text-blue-600 bg-blue-50' : 'text-gray-600 hover:bg-gray-50'
            }`}
            data-testid="mobile-nav-menu"
          >
            <Menu className="w-5 h-5 mb-0.5" />
            <span className="text-[10px] font-medium">Menü</span>
          </button>
        </div>
      </nav>
      )}
      
      {/* Spacer für Mobile Bottom Navigation - nur wenn nicht auf NewCase */}
      {!isNewCaseRoute && <div className="h-16 lg:hidden"></div>}
    </div>
  );
};

export default Layout;
